import { useCallback, useState } from "react";
import { VIEW_COLUMNS } from "../api/types";

/* Which columns a person wants in their CSV, remembered for them.
 *
 * docs/plan.md: the important fields are selected by default and any other
 * field on the record can be added, and the choice is remembered between
 * visits in the person's own browser. That is the one piece of state that
 * legitimately lives in the front end. It is a preference rather than a fact
 * about the data, nobody else needs it, and losing it costs a few clicks.
 *
 * The full list of columns comes from VIEW_COLUMNS in the generated types,
 * which is read from the live schema, so a column added to a view appears here
 * without anybody editing this file. A hand-written list would be the second
 * place a view's shape is described and would drift the first time it changed.
 */
const KEY_PREFIX = "saleshive.csv-columns.";

function read(key: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((v) => typeof v === "string") ? parsed : null;
  } catch {
    // A private window, blocked site data, or a value somebody hand-edited.
    // None of those is worth an error on a screen: the defaults are fine.
    return null;
  }
}

function write(key: string, value: string[]): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage being unavailable loses the preference and nothing else, so this
    // is one of the few places where carrying on quietly is the right answer.
  }
}

export type CsvColumns = {
  /** Every column the view returns, in the order the schema lists them. */
  all: readonly string[];
  /** The ones that go in the file, in the order they will appear. */
  chosen: string[];
  set: (next: string[]) => void;
  reset: () => void;
  isDefault: boolean;
};

export function useCsvColumns(
  screenKey: string,
  view: string,
  defaults: readonly string[],
): CsvColumns {
  const all = VIEW_COLUMNS[view] ?? defaults;
  // A saved choice is filtered against what the view still returns, so a column
  // removed from the engine cannot put a dead name into somebody's file.
  const saved = read(screenKey)?.filter((c) => all.includes(c));
  const [chosen, setChosen] = useState<string[]>(
    saved && saved.length ? saved : [...defaults],
  );

  const set = useCallback((next: string[]) => {
    setChosen(next);
    write(screenKey, next);
  }, [screenKey]);

  const reset = useCallback(() => {
    setChosen([...defaults]);
    write(screenKey, [...defaults]);
  }, [screenKey, defaults]);

  const isDefault =
    chosen.length === defaults.length && chosen.every((c, i) => c === defaults[i]);

  return { all, chosen, set, reset, isDefault };
}
